import {
  createContext,
  useContext,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

export type FeedbackPanelMode = 'capture' | 'manual';

export interface FeedbackPanelSession {
  /** Bumps on every open so the panel remounts with a clean draft. */
  id: number;
  mode: FeedbackPanelMode;
  /** The page the user was on when they opened the panel. */
  pageUrl: string;
  pageTitle: string;
}

interface OpenOptions {
  /** Called with the new feedback id after a successful submit. */
  onSubmitted?: (id: string) => void;
}

interface FeedbackPanelContextValue {
  session: FeedbackPanelSession | null;
  minimized: boolean;
  open: (mode: FeedbackPanelMode, options?: OpenOptions) => void;
  close: () => void;
  setMinimized: (minimized: boolean) => void;
  /** Runs the opener's `onSubmitted`, if any. */
  notifySubmitted: (id: string) => void;
}

const FeedbackPanelContext = createContext<FeedbackPanelContextValue | null>(
  null,
);

/**
 * Owns whether the feedback panel is open. The panel itself is mounted once
 * at the authenticated layout, outside the routed page, so a draft — text,
 * screenshots, uploads — survives navigating between pages while the user
 * collects more screenshots.
 */
export function FeedbackPanelProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<FeedbackPanelSession | null>(null);
  const [minimized, setMinimized] = useState(false);
  const nextId = useRef(1);
  const onSubmittedRef = useRef<((id: string) => void) | undefined>(undefined);

  function open(mode: FeedbackPanelMode, options?: OpenOptions) {
    setMinimized(false);
    // Re-opening while a draft exists just un-minimizes it: the draft and
    // the callback of whoever opened it are kept.
    if (session) return;
    onSubmittedRef.current = options?.onSubmitted;
    const id = nextId.current;
    nextId.current = id + 1;
    setSession({
      id,
      mode,
      pageUrl: window.location.href,
      pageTitle: document.title,
    });
  }

  function close() {
    onSubmittedRef.current = undefined;
    setSession(null);
    setMinimized(false);
  }

  function notifySubmitted(id: string) {
    onSubmittedRef.current?.(id);
  }

  return (
    <FeedbackPanelContext.Provider
      value={{ session, minimized, open, close, setMinimized, notifySubmitted }}
    >
      {children}
    </FeedbackPanelContext.Provider>
  );
}

export function useFeedbackPanel(): FeedbackPanelContextValue {
  const ctx = useContext(FeedbackPanelContext);
  if (!ctx) {
    throw new Error(
      'useFeedbackPanel must be used within FeedbackPanelProvider',
    );
  }
  return ctx;
}
